import React, { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { FormData, FormSettings } from '@/hooks/useAiPrayerAssistantForm';
import { useActionLoading } from '@/hooks/useActionLoading';
import { Textarea } from "@/components/ui/textarea";
import FormHeader from './components/FormHeader';
import PrayerTags from './components/PrayerTags';
import CompactBibleVerseSwitch from './components/CompactBibleVerseSwitch';
import CompactBilingualSwitch from './components/CompactBilingualSwitch';
import { criticalLogger } from '@/utils/logger';

interface AiPrayerFormProps {
  onClose: () => void;
  onComment?: (prayer: any) => void;
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
  formSettings: FormSettings;
  submitRequest: () => Promise<boolean>;
}

export default function AiPrayerForm({ 
  onClose, 
  onComment,
  formData,
  updateFormData,
  formSettings,
  submitRequest
}: AiPrayerFormProps) {
  const { toast } = useToast();
  const { setActionLoading, isActionLoading } = useActionLoading();

  // Defensive: Ensure formSettings and formData exist
  const maxRequestLength = formSettings?.maxRequestLength ?? 500;
  const availableTags = formSettings?.availableTags ?? [];
  const enableScripture = !!formSettings?.enableScripture;
  const enableBilingual = !!formSettings?.enableBilingual;

  useEffect(() => {
    try {
      const seed = sessionStorage.getItem('abiel_prefill');
      if (seed) {
        updateFormData({ requestText: seed });
        sessionStorage.removeItem('abiel_prefill');
      }
    } catch (err) {
      // Defensive: don't break UI if sessionStorage fails
      criticalLogger.error('Session storage error:', err);
    }
  }, [updateFormData]);

  const isLoading = isActionLoading('generate-prayer');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const value = formData?.requestText?.trim() ?? "";
    if (!value) {
      toast({
        title: "Prayer request required",
        description: "Please share what you'd like prayer for.",
        variant: "destructive"
      });
      return;
    }

    setActionLoading('generate-prayer', true, 'Generating prayer guide...');
    try {
      await submitRequest();
    } finally {
      setActionLoading('generate-prayer', false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full px-6" autoComplete="off">
      {/* Scrollable Form Content */}
      <div className="flex-1 space-y-4 overflow-y-auto pt-4">
        {/* Header Section */}
        <div className="flex-shrink-0">
          <FormHeader />
        </div>

        {/* Prayer Request Text Area */}
        <div className="space-y-2 px-2">
          <Textarea
            value={formData?.requestText ?? ""}
            onChange={(e) => updateFormData({ requestText: e.target.value })}
            placeholder="Share your heart... I'm here to pray with you"
            maxLength={maxRequestLength}
            disabled={isLoading}
            showCounter={true}
            className="min-h-[120px] bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-black dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 
                       rounded-lg leading-relaxed resize-none text-base py-3 px-3
                       focus:border-transparent focus:ring-2 focus:ring-app-gold focus:ring-offset-2
                       hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200"
            aria-label="Prayer request"
          />
        </div>

        {/* Side-by-side: Category & Bible Verse */}
        <div className="px-2">
          <div className="space-y-2">
            {/* Category & Bible Verse in 2-column grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* Category */}
              <div>
                <PrayerTags
                  selectedTags={formData?.selectedTags ?? []}
                  setSelectedTags={(tags) => updateFormData({ selectedTags: tags })}
                  availableTags={availableTags}
                  disabled={isLoading}
                />
              </div>

              {/* Include Bible Verse */}
              {enableScripture && (
                <div>
                  <CompactBibleVerseSwitch
                    value={!!formData?.includeBibleVerse}
                    onChange={(value) => updateFormData({ includeBibleVerse: value })}
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>

            {/* Bilingual Switch - separate row */}
            {enableBilingual && (
              <div>
                <CompactBilingualSwitch
                  value={!!formData?.bilingual}
                  onChange={(value) => updateFormData({ bilingual: value })}
                  disabled={isLoading}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Universal CTA Button */}
      <div className="flex-shrink-0 py-3">
        <button 
          type="submit" 
          disabled={isLoading || !(formData?.requestText?.trim())} 
          className="inline-flex items-center justify-center gap-2 bg-black hover:bg-black/90 dark:bg-gold dark:hover:bg-gold/90 text-white dark:text-black font-semibold px-6 py-2 rounded-full transition-colors w-full"
          aria-label="Generate Prayer Guide"
        >
          <span>{isLoading ? 'Generating Prayer Guide...' : 'Generate Prayer Guide'}</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
